import React, {useEffect, useState} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaPlus} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useParams} from "react-router";
import {useQuery} from "@tanstack/react-query";
import projectUtils from "../../../../utils/project-utils.js";
import placeService from "../../../../services/web/place-service.js";
import PlaceImage from "./components/place-image.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import optionsUtils from "../../../../utils/options-utils.js";
import {getCities, getNeighborhoods, getStreets} from "../../../../utils/location-utils.js";
import ModalAddress from "../../../../components/modal/modal-address.jsx";
import InputSwitch from "../../../../components/input/input-switch.jsx";
import {MaskDefault} from "../../../../utils/mask.js";
import Alert from "../../../../components/alert/alert.jsx";

const initialState = {
    city: "",
    cnpj: null,
    complement: null,
    contact: null,
    latitude: "",
    longitude: "",
    name: null,
    neighborhood: "",
    number: "",
    phone: null,
    state: "",
    status: true,
    street: "",
    type: "",
    zipcode: "",
    attach: null,
    attach_id: null,
}

const PlaceForm = () => {
    const navigate = navigateHook();

    const {id} = useParams()
    const [state, setState] = useState(initialState)
    const [types, setTypes] = useState([])
    const [open, setOpen] = useState(false);

    const {data, isLoading, refetch: refetchPlace} = useQuery({
        queryFn: () => placeService.get(id),
        queryKey: ['place', id],
        enabled: !!id
    });

    const {data: corporation} = useQuery({
        queryFn: () => placeService.entity(),
        queryKey: ['place-entity']
    });

    useEffect(() => {
        setState(initialState);
    }, [id]);

    useEffect(() => {
        const hasSavedCity = data?.city && String(data.id) === String(id);
        if (!corporation?.city || hasSavedCity) return;

        setState(prev => prev.city ? prev : {...prev, city: corporation.city});
    }, [corporation, data, id]);

    useEffect(() => {
        if (!data || String(data.id) !== String(id)) return;

        setState(prev => ({
            ...prev,
            ...data,
            city: data.city ? [data.city, data.state].filter(Boolean).join(" - ") : prev.city
        }));
    }, [data, id]);

    useQuery({
        queryFn: () =>  placeService.getTypes().then((data) => {
            const newData = []
            data.map(d => newData.push({ value: d.id, label: d.text }));

            setTypes(newData)
            return []
        }),
        queryKey: ['type']
    });

    const onChange = ({target: {value, id}}) => setState(prev => ({...prev, [id]: value}))

    const handleInputs = (id, value) => setState(prev => ({...prev, [id]: value}));

    const save = () => {
        const formData = new FormData();

        Object.keys(state)
            .filter(key => !["attach"].includes(key))
            .forEach(key => {
                let data = state[key];
                if (typeof data === 'object') data = data?.value ?? "";

                formData.append(key, data);
            });

        if (id) formData.append('_method', 'PUT');

        if (state.attach) formData.append("attach", state.attach);

        const response = id ? placeService.edit(id, formData) : placeService.create(formData);
        response
            .then(({data}) => {
                projectUtils.success(!id);

                if (id) {
                    refetchPlace();
                    return;
                }

                navigateToEdit(data)
            })
            .catch((error) => {
                console.error(error);

                const {message} = error.response.data;

                let errMessage = message ?? "Não foi possível salvar.";
                projectUtils.swalErrorMessage("", errMessage, "Ok");
            })

    }

    const destroy = () => {
        projectUtils.swalMessage(
            "warning",
            "Excluir",
            `Excluir o local "${state.name ?? ""}"?`,
            "Sim",
            true,
            "Não",
            ({isConfirmed}) => {
                if (isConfirmed) {
                    placeService.destroy(id).then(() => {
                        navigateToList()
                        projectUtils.success(true, "Excluído com sucesso");
                    })
                        .catch((error) => {
                            console.error(error);

                            const {message} = error.response.data;

                            let errMessage = message ?? "Não foi possível remover.";
                            projectUtils.swalErrorMessage("", errMessage, "Ok");
                        })
                }
            }
        );
    }

    const navigateToList = () => {
        let isBlank = false;
        let urlDev = `/place`;
        navigate(urlDev, isBlank);
    }

    const newStreet = () => {
        let isBlank = false;
        let urlDev = `/place/form`;
        navigate(urlDev, isBlank);
    }

    const navigateToEdit = (data) => {
        let isBlank = false;
        let urlDev = `/place/form/${data.id}`;
        navigate(urlDev, isBlank);
    }

    const handleMap = () => {
        setOpen(true);
    }

    const changeLocation = (latLng) => {
        handleInputs("latitude", latLng.lat())
        handleInputs("longitude", latLng.lng())
    }

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>
                    Local pré-cadastrado
                </Template.Title>
                {
                    id &&
                    <>
                        <Button target={"primary-outline"} onClick={() => newStreet()}>
                            <FaPlus/>
                            Adicionar local
                        </Button>
                    </>
                }
            </Template.Header>

            <Template.Body>
                <Card>
                    <Card.Body>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-2">
                                <PlaceImage setState={setState} state={state} />
                            </div>
                            <div className="col-span-10 relative">
                                <div className="grid grid-cols-12 gap-4 mb-3">
                                    <div className="col-1">
                                        <InputSwitch
                                            id="status"
                                            label="Ativo"
                                            checked={state.status}
                                            onChange={(checked) => handleInputs("status", checked)}
                                        />
                                    </div>
                                    <div className={"col-11"}></div>
                                    <div className="col-span-8">
                                        <InputForm id="name" value={state.name} onChange={onChange}
                                                   label="Nome *"/>
                                    </div>
                                    <div className="col-span-4">
                                        <SelectComponent
                                            name="type"
                                            value={types.find(t => t.value === state?.type)}
                                            options={types}
                                            label={"Tipo"}
                                            onChange={(selected) => handleInputs("type", selected?.value)}
                                        />
                                    </div>

                                    <div className="col-span-5">
                                        <SelectComponent
                                            loadOptions={getStreets}
                                            value={optionsUtils.getValue(state?.street)}
                                            isAsync={true}
                                            label={"Logradouro"}
                                            onChange={(selected) => handleInputs("street", selected?.value)}
                                            cacheOptions
                                            defaultOptions={[]}
                                        />
                                    </div>
                                    <div className="col-span-3">
                                        <InputForm id="number" value={state.number} onChange={onChange}
                                                   label="Número"/>
                                    </div>
                                    <div className="col-span-4">
                                        <SelectComponent
                                            loadOptions={getNeighborhoods}
                                            value={optionsUtils.getValue(state?.neighborhood)}
                                            isAsync={true}
                                            label={"Bairro"}
                                            onChange={(selected) => handleInputs("neighborhood", selected?.value)}
                                            cacheOptions
                                            defaultOptions={[]}
                                        />
                                    </div>

                                    <div className="col-span-5">
                                        <InputForm id="complement" value={state.complement} onChange={onChange}
                                                   label="Complemento"/>
                                    </div>
                                    <div className="col-span-3">
                                        <InputForm id="zipcode" value={state.zipcode} onChange={onChange}
                                                   label="CEP" mask={MaskDefault.cep}/>
                                    </div>
                                    <div className="col-span-4">
                                        <SelectComponent
                                            loadOptions={getCities}
                                            value={optionsUtils.getValue(state?.city)}
                                            isAsync={true}
                                            label={"Município - UF *"}
                                            onChange={(selected) => handleInputs("city", selected?.value)}
                                            cacheOptions
                                            defaultOptions={[]}
                                        />
                                    </div>

                                    <div className="col-span-3">
                                        <InputForm
                                            id={"cnpj"}
                                            label={"CNPJ"}
                                            classNameWrapper={"col-span-3"}
                                            value={state.cnpj}
                                            onChange={onChange}
                                            mask={MaskDefault.cnpj}
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <InputForm id="contact" value={state.contact} onChange={onChange}
                                                   label="Contato"/>
                                    </div>
                                    <div className="col-span-2">
                                        <InputForm id="phone" value={state.phone} onChange={onChange} mask={MaskDefault.phone}
                                                   label="Telefone"/>
                                    </div>

                                    <div className="col-span-2">
                                        <InputForm id="latitude" value={state.latitude} onChange={onChange}
                                                   label="Latitude"/>
                                    </div>
                                    <div className="col-span-2">
                                        <InputForm id="longitude" value={state.longitude} onChange={onChange}
                                                   label="Longitude"/>
                                    </div>
                                    <div className="col-span-1">
                                        <Button onClick={handleMap} className={"w-full"}>
                                            Mapa
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card className={"mt-2"}>
                    <Card.Body>
                        <div className="w-full flex justify-end">
                            <div className="flex gap-2 items-center">
                                {
                                    id &&
                                    <>
                                        {
                                            data && data?.hasCitizenAssociated &&
                                            <Alert className={"flex gap-2 items-center"} target={"warning"}>
                                                Não é possível excluir, pois existem cidadãos associados a esse local.
                                            </Alert>
                                        }

                                        <Button target={"danger"} onClick={destroy} disabled={data?.hasCitizenAssociated} actionFeedbackType='delete'>

                                            Excluir
                                        </Button>
                                    </>
                                }

                                <Button onClick={save} actionFeedbackType='save'>

                                    Salvar
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>

            <ModalAddress
                open={open}
                setOpen={setOpen}
                changeLocation={changeLocation}
                address={state}
            />
        </Template>
    );
};

export default PlaceForm;